var _sentryCollisionFreeGlobalObject = typeof window != "undefined" ? window : typeof global != "undefined" ? global : typeof self != "undefined" ? self : {};
_sentryCollisionFreeGlobalObject["__sentryRewritesTunnelPath__"] = undefined;
_sentryCollisionFreeGlobalObject["SENTRY_RELEASE"] = {"id":"s3QgXiOr6PEdMQtBKUZag"};
_sentryCollisionFreeGlobalObject["__sentryBasePath"] = undefined;
_sentryCollisionFreeGlobalObject["__rewriteFramesAssetPrefixPath__"] = "";

// This file configures the initialization of Sentry on the client.
// https://docs.sentry.io/platforms/javascript/guides/nextjs/

import * as Sentry from '@sentry/nextjs';

const sentryDsn = process.env.NEXT_PUBLIC_SENTRY_DSN;
const sentryProjectId = process.env.NEXT_PUBLIC_SENTRY_PROJECT_ID;
const isProduction = process.env.NODE_ENV === 'production';

// Check hostname to exclude staging/test environments
const isStagingOrTest = typeof window !== 'undefined'
  ? window.location.hostname.includes('staging') || window.location.hostname.includes('test')
  : false;

if (sentryDsn && sentryProjectId && isProduction && !isStagingOrTest) {
  Sentry.init({
    dsn: `${sentryDsn}/${sentryProjectId}`,
    tracesSampleRate: 1.0, // Adjust this value in production, or use tracesSampler for greater control
    debug: false,
    sendDefaultPii: false, // Don't send personally identifiable information

    // Note: Session Replay integration API changed in v8+
    // For v7, use Replay integration differently or omit if not needed
    // replaysOnErrorSampleRate: 1.0,
    // replaysSessionSampleRate: 0.1,

    // Filter out known third-party script errors that we cannot control
    beforeSend(event, hint) {
      const error = hint.originalException;
      const errorMessage = error?.toString() || '';
      const eventValue = event.exception?.values?.[0]?.value || '';

      // Filter webkit-related errors from third-party scripts (iOS)
      if (
        errorMessage.includes('window.webkit') ||
        errorMessage.includes('webkit.messageHandlers') ||
        eventValue.includes('webkit')
      ) {
        // Check if error is from third-party script (not our code)
        const frames = event.exception?.values?.[0]?.stacktrace?.frames || [];
        const isThirdParty = frames.some(frame =>
          !frame.filename?.includes('schedule.thrivelab.com') &&
          !frame.in_app
        );

        if (isThirdParty) {
          // Don't send this error to Sentry
          return null;
        }
      }

      // Filter postMessage/Java bridge errors from Android WebView (Facebook browser, etc.)
      // These are WebView bugs we cannot control - always filter them
      if (
        errorMessage.includes('Java object is gone') ||
        errorMessage.includes('postMessage') ||
        errorMessage.includes('JavaBridge') ||
        eventValue.includes('Java object is gone') ||
        eventValue.includes('invoking postMessage')
      ) {
        return null;
      }

      // Filter client-side network errors (user's network issues, not ours)
      if (
        errorMessage.includes('Network Error') ||
        errorMessage.includes('Load failed') ||
        errorMessage.includes('Failed to fetch') ||
        eventValue.includes('Network Error') ||
        eventValue.includes('Load failed') ||
        eventValue.includes('Failed to fetch')
      ) {
        // Check if this is from axios or fetch (client network request)
        const frames = event.exception?.values?.[0]?.stacktrace?.frames || [];
        const isNetworkRequest = frames.some(frame =>
          frame.filename?.includes('axios') ||
          frame.function?.includes('fetch') ||
          frame.function?.includes('XMLHttpRequest')
        );

        // Filter network errors that don't have meaningful stack traces
        // These are typically user network issues we cannot control
        if (isNetworkRequest || frames.length === 0) {
          return null;
        }
      }

      // Filter autofill bridge errors from Facebook/browser autofill systems
      if (
        errorMessage.includes('setContactAutofillValuesFromBridge') ||
        errorMessage.includes('AutofillValuesFromBridge') ||
        eventValue.includes('setContactAutofillValuesFromBridge')
      ) {
        // These are browser autofill errors we cannot control
        return null;
      }

      // Filter "Failed to fetch" errors from third-party tracking scripts
      if (
        (errorMessage.includes('Failed to fetch') || eventValue.includes('Failed to fetch')) &&
        event.exception?.values?.[0]?.stacktrace?.frames?.some(frame =>
          frame.filename?.includes('ct/lib/main') ||  // Pinterest conversion tracking
          frame.filename?.includes('pinimg.com') ||   // Pinterest
          frame.filename?.includes('fbevents.js') ||  // Facebook Pixel
          frame.filename?.includes('gtm.js') ||       // Google Tag Manager
          frame.filename?.includes('clarity.ms') ||   // Microsoft Clarity
          frame.filename?.includes('nextdoor.com') || // Nextdoor
          frame.filename?.includes('shareasale.com') || // ShareASale
          frame.filename?.includes('analytics') ||    // Generic analytics
          frame.filename?.includes('tracker') ||      // Generic trackers
          !frame.in_app                               // Any non-app code
        )
      ) {
        // Third-party tracking scripts being blocked by ad blockers or privacy tools
        return null;
      }

      return event;
    },
  });
}
